"use client";

import { useEffect } from "react";
import Link from "@/components/LocalizedLink";
import { CONTACT_EMAIL } from "@/lib/constants";

export default function ErrorPage({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    // Optionally log to external error reporting
  }, [error]);

  return (
    <section className="min-h-[80vh] flex flex-col items-center justify-center px-4 text-center">
      {/* Decorative background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="network-bg opacity-40" />
      </div>

      <div className="relative z-10 max-w-lg mx-auto">
        {/* Error icon */}
        <div className="relative mb-6 w-20 h-20 mx-auto">
          <div className="absolute inset-0 rounded-full bg-red-500/10 border border-red-500/20 flex items-center justify-center">
            <svg
              className="w-8 h-8 text-red-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth="1.5"
            >
              <path
                strokeLinecap="round"
                d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
              />
            </svg>
          </div>
          <div className="absolute inset-0 rounded-full bg-red-500/5 animate-ping" />
        </div>

        <h1 className="text-2xl sm:text-3xl font-bold text-foreground mb-3">
          Something Went Wrong
        </h1>
        <p className="text-muted-foreground mb-8 max-w-md mx-auto">
          An unexpected error occurred. Our network operations center has been
          notified and is looking into it.
        </p>

        {error.digest && (
          <p className="text-xs text-muted font-mono mb-6">
            Error ID: {error.digest}
          </p>
        )}

        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <button
            onClick={reset}
            className="inline-flex items-center gap-2 px-6 py-2.5 text-sm font-medium text-background bg-accent rounded-lg hover:bg-accent/90 transition-colors"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path
                strokeLinecap="round"
                d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182"
              />
            </svg>
            Try Again
          </button>
          <Link
            href="/"
            className="inline-flex items-center gap-2 px-6 py-2.5 text-sm font-medium text-foreground border border-border rounded-lg hover:border-primary/40 hover:bg-hover-bg transition-colors"
          >
            Back to Home
          </Link>
        </div>

        {/* NOC contact */}
        <div className="mt-10 text-xs text-muted">
          Persistent issues? Contact NOC at{" "}
          <a
            href={`mailto:${CONTACT_EMAIL.noc}`}
            className="text-primary hover:text-primary-light transition-colors"
          >
            {CONTACT_EMAIL.noc}
          </a>
        </div>
      </div>
    </section>
  );
}
